<template>
  <div>
    <a-action-buttons-bar
        class="mb-4"
        :options="{ selected: table.selected}"
        :buttons="buttons">
    </a-action-buttons-bar>

    <v-expand-transition>
      <v-form ref="form" v-model="create.valid" v-show="create.opened" class="mb-4">
        <export-form :only-reports="true" :ids="ids" :value.sync="create.fields"></export-form>
        <v-row>
          <v-col cols="12">
            <v-text-field
                filled
                :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-at' : ''"
                v-model="create.fields.emails"
                :label="$t('email-field')"
                :hint="$t('email-field-hint', { count: emailsCount })"
                persistent-hint
                :rules="create.rules.emails">
            </v-text-field>
          </v-col>
          <v-col cols="12" class="text-right">
            <v-btn
                :disabled="!create.valid"
                :loading="create.loading"
                color="success"
                @click="createExport"
            >
              {{ $t('button-create-action') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-expand-transition>

    <v-text-field
        class="mb-4"
        dense
        rounded
        filled
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        :label="$t('actions.search-three-dots')"
        single-line
        hide-details
        append-icon="mdi-close"
        @click:append="search = ''"
    ></v-text-field>

    <v-data-table
        dense
        must-sort
        :items="table.items.all"
        :options.sync="table.options"
        :server-items-length="table.items.length"
        :loading="table.items.loading"
        :headers="table.headers"
        :show-select="$vuetify.breakpoint.mdAndUp"
        v-model="table.selected"
        itemid="id"
    >
      <template v-slot:item.type="{ item }">
        <v-edit-dialog
            @open="dialog.value = item.type"
            @save="$repos.scheduledExports.update(item.id, { type: dialog.value})"
            large
            :cancel-text="$t('actions.cancel')"
            :save-text="$t('actions.save')">
          <span class="editable">
            {{ $t('dates.' + item.type) }}
          </span>
          <edit-dialog-pencil></edit-dialog-pencil>
          <template v-slot:input>
            <v-select
                v-model="dialog.value"
                :items="create.types"
                hide-details
            >
            </v-select>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:item.handler_options="{ item }">
        <div class="editable v-small-dialog__activator">
          <export-options-select
              :text-as-input="true"
              :enable-save="true"
              @save="$repos.scheduledExports.update(item.id, { handler_options: item.handler_options})"
              :value.sync="item.handler_options">
          </export-options-select>
          <edit-dialog-pencil></edit-dialog-pencil>
        </div>
      </template>
      <template v-slot:item.transport_options="{ item }">
        <v-edit-dialog
            @open="dialog.value = Object.assign({}, item.transport_options)"
            @save="$repos.scheduledExports.update(item.id, { transport_options: dialog.value})"
            large
            :cancel-text="$t('actions.cancel')"
            :save-text="$t('actions.save')">
          <span class="editable">
            {{ item.transport_options.emails }}
          </span>
          <edit-dialog-pencil></edit-dialog-pencil>
          <template v-slot:input>
            <v-text-field
                v-if="dialog.value instanceof Object && 'emails' in dialog.value"
                v-model="dialog.value.emails"
                :rules="create.rules.emails"
                hide-details>
            </v-text-field>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:item.monitors_ways="{ item }">
        <v-edit-dialog
            @open="dialog.value = item.monitors_ways"
            @save="$repos.scheduledExports.update(item.id, { monitors_ways: dialog.value})"
            large
            :cancel-text="$t('actions.cancel')"
            :save-text="$t('actions.save')">
          <span class="editable font-italic" v-if="item.monitors_ways.length">{{ item.monitors_ways.length }} {{ $t('fields.elements') }}</span>
          <span class="editable font-italic" v-else>{{ $t('fields.all') }}</span>
          <edit-dialog-pencil></edit-dialog-pencil>
          <template v-slot:input>
            <v-row style="margin: 0">
              <v-col>
                <relation-select
                    :icon="false"
                    :entities="['monitors', 'websites', 'tags']"
                    :value.sync="dialog.value"
                    :closeOnContentClick="true">
                </relation-select>
              </v-col>
            </v-row>
          </template>
        </v-edit-dialog>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import ExportForm from '@/views/Pages/PositionMonitoring/Dialogs/ManageExportsDialog/Components/ExportForm'
import EditDialogPencil from '@/views/Common/Components/Table/EditDialogPencil'
import RelationSelect from '@/views/Common/Components/Selects/RelationSelect'
import ExportOptionsSelect from '@/views/Common/Components/Selects/ExportOptionsSelect'

export default {
  name: 'ScheduleExports',
  components: { ExportOptionsSelect, RelationSelect, EditDialogPencil, ExportForm },
  props: ['ids'],
  data () {
    return {
      create: {
        opened: false,
        valid: false,
        loading: false,
        fields: {
          emails: '',
        },
        rules: {
          emails: [
            v => !!v || this.$t('email-required'),
            () => this.emailsCount > 10 ? this.$t('too-many-emails') : null,
          ],
        },
        types: [
          {
            text: this.$t('type-report-daily'),
            value: 'daily'
          },
          {
            text: this.$t('type-report-weekly'),
            value: 'weekly'
          },
          {
            text: this.$t('type-report-monthly'),
            value: 'monthly'
          },
        ]
      },
      dialog: {
        value: null
      },
      search: '',
      table: {
        options: {},
        items: this.$repos.scheduledExports.createDatatableCollection(),
        selected: [],
        headers: [
          { text: this.$t('column-type'), value: 'type', sortable: false },
          { text: this.$t('column-handler-options'), value: 'handler_options', sortable: false },
          { text: this.$t('column-transport-options'), value: 'transport_options', sortable: false },
          { text: this.$t('link-to'), value: 'monitors_ways', sortable: false }
        ],
      },
      buttons: [
        {
          color: 'success',
          text: this.$t('button-create'),
          icon: 'mdi-plus',
          click: () => this.create.opened = !this.create.opened,
        },
        {
          color: 'error',
          confirmation: true,
          text: ({ selected }) => this.$t('actions.remove') + ' (' + selected.length + ')',
          icon: 'mdi-delete',
          disabled: ({ selected }) => selected.length === 0,
          click: ({ selected }) => this.removeExports(selected.map(selected => selected.id)),
        }
      ]
    }
  },
  watch: {
    'table.options': {
      handler: async function () {
        await this.fetch()
      },
      deep: true
    },
    search: debounce(async function () {
      await this.fetch()
    }, 250)
  },
  computed: {
    emailsCount() {
      return this.create.fields.emails.split(',').length
    }
  },
  methods: {
    async fetch () {
      if (Object.keys(this.table.options).length > 0) {
        await this.table.items.fetch({
          ...this.table.options,
          search: this.search,
          with: ['monitors'],
        })
      }
    },
    handleTableSelect (ids) {
      this.table.selected = ids
    },
    async createExport () {
      try {
        this.create.loading = true
        this.table.selected = []

        await this.$repos.scheduledExports.create({
          type: this.create.fields.type,
          monitors_ways: this.create.fields.targets,
          handler_options: this.create.fields.options.report,
          transport_options: {
            emails: this.create.fields.emails
          }
        })

        this.create.fields.emails = ''

        this.$refs.form.resetValidation()
      } finally {
        this.create.loading = false
      }
    },
    async removeExports (ids) {
      this.table.selected = []
      await this.$repos.scheduledExports.remove(ids)
    }
  }
}
</script>

<i18n>
{
	"en": {
		"button-create": "Create",
		"button-create-action": "Create a new export",
		"column-handler-options": "Personalization Options",
		"column-transport-options": "Email",
		"column-type": "Export Type",
		"email-field": "Email(s)",
		"email-field-hint": "You can specify up to 10 emails separated by commas: you entered {count} email(s)",
		"email-required": "At least one email is required",
		"link-to": "Linked to",
		"too-many-emails": "You cannot specify more than 10 emails",
		"type-report-daily": "Daily position report",
		"type-report-monthly": "Monthly position report",
		"type-report-weekly": "Weekly position report"
	},
	"es": {
		"button-create": "Crear",
		"button-create-action": "Crear una nueva exportación",
		"column-handler-options": "Opciones de personalización",
		"column-transport-options": "Email",
		"column-type": "Tipo de exportación",
		"email-field": "Correos electrónicos",
		"email-field-hint": "Puede especificar hasta 10 correos electrónicos separados por comas: ingresó {count} correos electrónicos",
		"email-required": "Se requiere al menos un correo electrónico",
		"link-to": "Vinculado a",
		"too-many-emails": "No puede especificar más de 10 correos electrónicos",
		"type-report-daily": "Informe diario de posición",
		"type-report-monthly": "Informe de posición mensual",
		"type-report-weekly": "Informe de posición semanal"
	},
	"fr": {
		"button-create": "Ajouter",
		"button-create-action": "Créer un nouvel export",
		"column-handler-options": "Options de personnalisation",
		"column-transport-options": "Emails",
		"column-type": "Type d'export",
		"email-field": "Email(s)",
		"email-field-hint": "Vous pouvez spécifier jusqu'à 10 emails séparés par des virgules : vous avez saisi {count} email(s)",
		"email-required": "Au moins un email est requis",
		"link-to": "Lié à",
		"too-many-emails": "Vous ne pouvez pas spécifier plus de 10 emails",
		"type-report-daily": "Rapport quotidien de positions",
		"type-report-monthly": "Rapport mensuel de positions",
		"type-report-weekly": "Rapport hebdomadaire de positions"
	},
	"it": {
		"button-create": "Aggiungi",
		"button-create-action": "Crea una nuova esportazione",
		"column-handler-options": "Opzioni di personalizzazione",
		"column-transport-options": "Email",
		"column-type": "Tipo di esportazione",
		"email-field": "Email",
		"email-field-hint": "Puoi specificare fino a 10 email separate da virgole: hai inserito {count} email",
		"email-required": "È richiesta almeno un'email",
		"link-to": "Collegato a",
		"too-many-emails": "Non puoi specificare più di 10 email",
		"type-report-daily": "Rapporto giornaliero di posizioni",
		"type-report-monthly": "Rapporto mensile di posizioni",
		"type-report-weekly": "Rapporto settimanale di posizioni"
	},
	"pt": {
		"button-create": "Criar",
		"button-create-action": "Criar uma nova exportação",
		"column-handler-options": "Opções de personalização",
		"column-transport-options": "Email",
		"column-type": "Tipo de exportação",
		"email-field": "Email(s)",
		"email-field-hint": "Pode especificar até 10 emails separados por vírgulas: introduziu {count} email(s)",
		"email-required": "É necessário pelo menos um email",
		"link-to": "Associado a",
		"too-many-emails": "Não pode especificar mais de 10 emails",
		"type-report-daily": "Relatório diário de posições",
		"type-report-monthly": "Relatório mensal de posições",
		"type-report-weekly": "Relatório semanal de posições"
	},
	"pl": {
		"button-create": "Utwórz",
		"button-create-action": "Utwórz nowy eksport",
		"column-handler-options": "Opcje personalizacji",
		"column-transport-options": "Email",
		"column-type": "Typ eksportu",
		"email-field": "Email(e)",
		"email-field-hint": "Możesz podać do 10 adresów e-mail oddzielonych przecinkami: podałeś {count} email(e)",
		"email-required": "Wymagany jest co najmniej jeden adres e-mail",
		"link-to": "Powiązano z",
		"too-many-emails": "Nie możesz podać więcej niż 10 adresów e-mail",
		"type-report-daily": "Dzienny raport pozycji",
		"type-report-monthly": "Miesięczny raport pozycji",
		"type-report-weekly": "Tygodniowy raport pozycji"
	}
}
</i18n>